/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Calendar grid matching and virtual/in-person appointment slot helpers.
 * @module utils/calendar/calendarAppointmentUtils
 */

/** @param {Object} appointment @returns {boolean} */
export const isVirtualAppointment = (appointment) =>
  Boolean(
    appointment.teamsJoinUrl &&
      appointment.horaInicioCitaVirtual &&
      appointment.diaCitaVirtual &&
      appointment.estadoCitaVirtual === "approved",
  );

const parseVirtualAppointmentTime = (appointment) => {
  if (appointment.horaInicioCitaVirtual.includes("T")) {
    const virtualDateTime = new Date(appointment.horaInicioCitaVirtual);
    return {
      hour: virtualDateTime.getUTCHours(),
      minute: virtualDateTime.getUTCMinutes(),
    };
  }

  const [hour, minute] = appointment.horaInicioCitaVirtual.split(":").map(Number);
  return { hour, minute };
};

export const appointmentMatchesDateTimeSlot = (appointment, date, timeSlot) => {
  if (isVirtualAppointment(appointment)) {
    const virtualDate = new Date(appointment.diaCitaVirtual + "T00:00:00");
    const { hour, minute } = parseVirtualAppointmentTime(appointment);

    return (
      virtualDate.toDateString() === date.toDateString() &&
      hour === timeSlot.hour &&
      minute === timeSlot.minute
    );
  }

  if (!appointment.horaInicio || appointment.estado !== "approved") {
    return false;
  }

  const appointmentDate = new Date(appointment.fecha + "T00:00:00");
  const [appointmentHour, appointmentMinute] = appointment.horaInicio
    .split(":")
    .map(Number);

  return (
    appointmentDate.toDateString() === date.toDateString() &&
    appointmentHour === timeSlot.hour &&
    appointmentMinute === timeSlot.minute
  );
};

export const getAppointmentsForDateTime = (appointments, date, timeSlot) =>
  appointments.filter((appointment) =>
    appointmentMatchesDateTimeSlot(appointment, date, timeSlot),
  );

const appointmentsOverlap = (
  proposedStartMinutes,
  proposedEndMinutes,
  appointment,
) => {
  const [startHour, startMinute] = appointment.horaInicio.split(":").map(Number);
  const [endHour, endMinute] = appointment.horaFin.split(":").map(Number);
  const appointmentStartMinutes = startHour * 60 + startMinute;
  const appointmentEndMinutes = endHour * 60 + endMinute;

  return (
    (proposedStartMinutes >= appointmentStartMinutes &&
      proposedStartMinutes < appointmentEndMinutes) ||
    (proposedEndMinutes > appointmentStartMinutes &&
      proposedEndMinutes <= appointmentEndMinutes) ||
    (proposedStartMinutes <= appointmentStartMinutes &&
      proposedEndMinutes >= appointmentEndMinutes)
  );
};

const hasConsecutiveBlockConflict = (allCompanyAppointments, timeSlot) => {
  allCompanyAppointments.sort((a, b) => a.start - b.start);

  for (let i = 0; i < allCompanyAppointments.length - 1; i++) {
    const current = allCompanyAppointments[i];
    const next = allCompanyAppointments[i + 1];

    if (current.end === next.start) {
      let blockSize = 2;
      let j = i + 1;

      while (
        j < allCompanyAppointments.length - 1 &&
        allCompanyAppointments[j].end === allCompanyAppointments[j + 1].start
      ) {
        blockSize++;
        j++;
      }

      if (blockSize >= 2) {
        return true;
      }
    }
  }

  if (allCompanyAppointments.length >= 2) {
    for (let i = 0; i < allCompanyAppointments.length - 1; i++) {
      const current = allCompanyAppointments[i];
      const next = allCompanyAppointments[i + 1];

      if (
        current.end === next.start &&
        (current.isProposed || next.isProposed)
      ) {
        const totalMinutes = next.end - current.start;
        const timeSlotsInBlock = totalMinutes / timeSlot.durationMinutes;
        if (timeSlotsInBlock >= 2) {
          return true;
        }
      }
    }
  }

  return false;
};

export const hasCompanyUserConflict = ({
  date,
  timeSlot,
  appointmentToCheck,
  allAppointments,
  appointments,
}) => {
  if (!appointmentToCheck?.idEmpresaUsuarioRemitente) {
    return false;
  }

  const appointmentsToCheck =
    allAppointments && allAppointments.length > 0
      ? allAppointments
      : appointments;

  const dayAppointments = appointmentsToCheck.filter((appointment) => {
    if (!appointment.horaInicio || appointment.estado !== "approved") {
      return false;
    }
    const appointmentDate = new Date(appointment.fecha + "T00:00:00");
    return appointmentDate.toDateString() === date.toDateString();
  });

  const sameCompanyAppointments = dayAppointments.filter(
    (appointment) =>
      appointment.idEmpresaUsuarioRemitente ===
        appointmentToCheck.idEmpresaUsuarioRemitente &&
      appointment._id !== appointmentToCheck._id,
  );

  if (sameCompanyAppointments.length === 0) {
    return false;
  }

  const proposedStartMinutes = timeSlot.hour * 60 + timeSlot.minute;
  const proposedEndMinutes = proposedStartMinutes + timeSlot.durationMinutes;

  for (const appointment of sameCompanyAppointments) {
    if (
      appointmentsOverlap(
        proposedStartMinutes,
        proposedEndMinutes,
        appointment,
      )
    ) {
      return true;
    }
  }

  const allCompanyAppointments = sameCompanyAppointments.map((appointment) => {
    const [startHour, startMinute] = appointment.horaInicio
      .split(":")
      .map(Number);
    const [endHour, endMinute] = appointment.horaFin.split(":").map(Number);
    return {
      start: startHour * 60 + startMinute,
      end: endHour * 60 + endMinute,
      id: appointment._id,
      isProposed: false,
    };
  });

  allCompanyAppointments.push({
    start: proposedStartMinutes,
    end: proposedEndMinutes,
    id: "proposed",
    isProposed: true,
  });

  return hasConsecutiveBlockConflict(allCompanyAppointments, timeSlot);
};

export const getAppointmentType = (appointment) => {
  if (isVirtualAppointment(appointment)) {
    if (appointment.confirmado === true) {
      return "virtualConfirmed";
    }
    return "virtual";
  }

  if (
    appointment.estado === "approved" &&
    appointment.horaInicio &&
    appointment.horaFin
  ) {
    if (appointment.confirmado === true) {
      return "confirmed";
    }
    return "set";
  }

  return "default";
};

export const getAppointmentTimingStatus = (appointment) => {
  if (!appointment.horaInicio || !appointment.horaFin) {
    return "unassigned";
  }

  const now = new Date();
  const appointmentDate = new Date(appointment.fecha + "T00:00:00");
  const startTimeParts = appointment.horaInicio.split(":");
  const endTimeParts = appointment.horaFin.split(":");

  const appointmentStartTime = new Date(appointmentDate);
  appointmentStartTime.setHours(
    parseInt(startTimeParts[0], 10),
    parseInt(startTimeParts[1], 10),
    0,
    0,
  );

  const appointmentEndTime = new Date(appointmentDate);
  appointmentEndTime.setHours(
    parseInt(endTimeParts[0], 10),
    parseInt(endTimeParts[1], 10),
    0,
    0,
  );

  if (now > appointmentEndTime) {
    return "past";
  }
  if (now >= appointmentStartTime && now <= appointmentEndTime) {
    return "current";
  }
  return "future";
};
